/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import { If, Then, Else } from "react-if";
import { Button, Spinner, FlexContainer, Text, theme } from "@nordcloud/gnui";
import { ContactsInput } from "~/components/Inputs";
import { ERROR_TEXT } from "~/constants";
import { useToggle } from "~/hooks";
import { useBillingPeriod } from "~/services/customers";
import { showError, showSuccess } from "~/services/toast";
import { generateActionSuccessText } from "~/tools";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Props = {
  property: "contactIds" | "ownerId";
  type: "application" | "environment";
  query: any;
  mutation: any;
  queryVariables: { id: string; [key: string]: unknown };
  readOnly?: boolean;
  onUpdate?: () => void;
};

export function InlineContacts({
  query,
  mutation,
  type,
  property,
  queryVariables,
  readOnly = false,
  onUpdate,
}: Props) {
  const { register, handleSubmit, setValue } = useForm();
  const [contacts, setContacts] = React.useState<string[]>([]);
  const [isDirty, setIsDirty] = React.useState(false);

  const [isEditMode, toggleEditMode] = useToggle(false);

  React.useEffect(() => {
    register(property);
  }, [register, property]);

  const { data, loading, refetch } = query({
    variables: queryVariables,
  });

  const { isPreviousBillingPeriod } = useBillingPeriod();

  const [update, { loading: mutationLoading }] = mutation();

  const onEdit = async (values: any) => {
    try {
      closeEditMode();
      const { id, ...input } = values;
      if (type === "application") {
        await update({
          variables: {
            nid: id,
            input: { ...input },
          },
        });
      } else {
        const { contactId, ...restValues } = input;
        await update({
          variables: {
            nid: id,
            input: {
              ...(contactId && { contactIds: [contactId] }),
              ...restValues,
            },
          },
        });
      }

      const afterUpdateAction = onUpdate ?? refetch;
      await afterUpdateAction();

      showSuccess(
        generateActionSuccessText(`${type} ${property}`)()("updated")()
      );
    } catch {
      showError(ERROR_TEXT.default);
    }
  };

  const closeEditMode = () => {
    toggleEditMode();
    setIsDirty(false);
  };

  const isLoading = loading || mutationLoading;
  const isReadOnly = readOnly || isPreviousBillingPeriod;

  if (isLoading) {
    return (
      <FlexContainer>
        <Spinner size="sm" />
        <Text
          weight="regular"
          tag="div"
          css={{ marginLeft: theme.spacing.spacing04 }}
        >
          Loading...
        </Text>
      </FlexContainer>
    );
  }

  return (
    <If condition={isEditMode}>
      <Then>
        <form
          css={{ display: "block", width: "100%" }}
          onSubmit={handleSubmit(onEdit)}
        >
          <input
            type="hidden"
            {...register("id")}
            defaultValue={queryVariables.id}
          />
          <If condition={type === "environment"}>
            <input type="hidden" {...register("name")} />
          </If>
          <FlexContainer columnGap={theme.spacing.spacing02}>
            <ContactsInput
              small
              value={contacts}
              onChange={(newPerson) => {
                setContacts([newPerson]);
                setValue(
                  property,
                  property === "ownerId" ? newPerson : [newPerson]
                );
                setIsDirty(true);
              }}
            />
            <If condition={isDirty || property === "ownerId"}>
              <Then>
                <Button type="submit" icon="save" size="md" severity="low">
                  Save
                </Button>
              </Then>
              <Else>
                <Button
                  icon="trash"
                  size="md"
                  severity="low"
                  type="submit"
                  onClick={() => {
                    setContacts([]);
                    setValue(property, []);
                  }}
                >
                  Delete
                </Button>
              </Else>
            </If>
            <Button
              icon="close"
              size="md"
              severity="low"
              onClick={closeEditMode}
            >
              Cancel
            </Button>
          </FlexContainer>
        </form>
      </Then>
      <Else>
        <If condition={isReadOnly}>
          <Then>
            <FlexContainer
              alignItems="center"
              columnGap={theme.spacing.spacing02}
            >
              <Text as="div" m="0">
                {property === "ownerId"
                  ? data?.[type]?.owner?.name ?? ""
                  : (data?.[type]?.contacts &&
                      data?.[type]?.contacts[0]?.name) ??
                    ""}
              </Text>
            </FlexContainer>
          </Then>
          <Else>
            <FlexContainer
              alignItems="center"
              columnGap={theme.spacing.spacing02}
            >
              <Text as="div" m="0">
                {property === "ownerId"
                  ? data?.[type]?.owner?.name ?? ""
                  : (data?.[type]?.contacts &&
                      data?.[type]?.contacts[0]?.name) ??
                    ""}
              </Text>
              <Button
                severity="low"
                size="sm"
                icon="edit"
                css={{ margin: 0, borderRadius: "22px", padding: "0.3rem" }}
                onClick={() => {
                  setContacts(
                    property === "ownerId"
                      ? [data?.[type]?.owner?.id ?? ""]
                      : [data?.[type]?.contacts?.[0]?.id ?? ""]
                  );
                  toggleEditMode();
                }}
              />
            </FlexContainer>
          </Else>
        </If>
      </Else>
    </If>
  );
}
